import React from 'react';
import { Skeleton, Spin, Empty, Result, Button, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface TableLoadingProps {
  rows?: number;
  columns?: number;
}

export const TableLoading: React.FC<TableLoadingProps> = ({ rows = 10, columns = 5 }) => {
  return (
    <div style={{ padding: '16px' }}>
      <Skeleton.Button active style={{ width: 200, marginBottom: 16 }} />
      <Skeleton active paragraph={{ rows }} />
    </div>
  );
};

interface LoadingSpinnerProps {
  tip?: string;
  size?: 'small' | 'default' | 'large';
  delay?: number;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  tip = 'Loading...',
  size = 'default',
  delay = 200
}) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '32px',
      minHeight: '200px'
    }}>
      <Spin size={size} tip={tip} delay={delay} />
    </div>
  );
};

interface EmptyStateProps {
  description?: string;
  showReset?: boolean;
  onReset?: () => void;
  image?: 'default' | 'simple';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  description = 'No data found',
  showReset = false,
  onReset,
  image = 'default'
}) => {
  return (
    <Empty
      image={image === 'simple' ? Empty.PRESENTED_IMAGE_SIMPLE : Empty.PRESENTED_IMAGE_DEFAULT}
      description={description}
    >
      {showReset && onReset && (
        <Button onClick={onReset} type="primary">
          <FontAwesomeIcon icon="fa-solid fa-redo" /> Reset Filters
        </Button>
      )}
    </Empty>
  );
};

interface ErrorStateProps {
  error: Error | string;
  onRetry?: () => void;
  showDetails?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  error,
  onRetry,
  showDetails = process.env.NODE_ENV === 'development'
}) => {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorStack = typeof error === 'object' ? error.stack : undefined;

  return (
    <Result
      status="error"
      title="Failed to load data"
      subTitle={errorMessage}
      extra={
        <Space direction="vertical">
          {onRetry && (
            <Button type="primary" onClick={onRetry}>
              <FontAwesomeIcon icon="fa-solid fa-redo" /> Try Again
            </Button>
          )}
          {showDetails && errorStack && (
            <details style={{ marginTop: 16, textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer' }}>Error Details</summary>
              <pre style={{
                fontSize: '0.85em',
                overflow: 'auto',
                background: '#f5f5f5',
                padding: '8px',
                borderRadius: '4px',
                marginTop: '8px'
              }}>
                {errorStack}
              </pre>
            </details>
          )}
        </Space>
      }
    />
  );
};

interface LoadingOverlayProps {
  loading: boolean;
  tip?: string;
  children: React.ReactNode;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  loading,
  tip = 'Processing...',
  children
}) => {
  return (
    <Spin spinning={loading} tip={tip} style={{ width: '100%' }}>
      {children}
    </Spin>
  );
};

// Custom hook for managing loading states
export const useLoadingState = <T,>(
  initialData?: T
): {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  setData: (data: T) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
} => {
  const [data, setData] = React.useState<T | undefined>(initialData);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const reset = React.useCallback(() => {
    setData(initialData);
    setLoading(false);
    setError(null);
  }, [initialData]);

  return {
    data,
    loading,
    error,
    setData,
    setLoading,
    setError,
    reset,
  };
};

// Namespace export for convenient access
export const LoadingStates = {
  Table: TableLoading,
  Spinner: LoadingSpinner,
  Empty: EmptyState,
  Error: ErrorState,
  Overlay: LoadingOverlay,
  FullPage: (props: LoadingSpinnerProps) => <LoadingSpinner {...props} size="large" />,
  Content: (props?: LoadingSpinnerProps) => <LoadingSpinner {...props} />,
};
